/**
 * PortalLoginPage — Login del portal aislado del colaborador (Caso A).
 *
 * Ruta: /colaboradores/login
 * No usa el authStore del ERP — usa portalAuthStore.
 *
 * Las formas de entrar salen de lo que el tenant configuró en `/config/sso`:
 * Google y SAML si están activos, y la contraseña —con su recuperación— solo si
 * el tenant la permite. Con Google, quien tiene usuario del ERP también entra
 * acá: el backend lo reconoce y lo lleva a su portal por esa vía.
 */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Loader2, ShieldCheck } from 'lucide-react'
import { usePortalLogin } from '../hooks/useEmployeePortal'
import { usePortalAuthStore } from '@/store/portalAuthStore'
import { useSSOConfig } from '@/modules/auth/hooks/useAuth'
import apiClient from '@/lib/apiClient'
import { extractErrorMessage } from '@/lib/errorUtils'
import { EnvBanner } from '@/components/EnvBanner'
import { GoogleIcon } from '@/components/GoogleIcon'
import { googleDisponible, iniciarLoginGoogle } from '@/lib/googleOAuth'
import { enElPortal, tomarDestino } from '@/lib/destinoTrasLogin'

export default function PortalLoginPage() {
  const navigate = useNavigate()
  const { setTokens } = usePortalAuthStore()
  const loginMutation = usePortalLogin()
  const { data: ssoConfig, isLoading: ssoLoading } = useSSOConfig()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [ssoPending, setSsoPending] = useState(false)

  // La política la define el tenant: con `sso_only` el formulario de contraseña
  // no se dibuja, porque es justo el mecanismo que la empresa quiso eliminar.
  const showSSOButton = ssoConfig?.portal_sso_enabled === true
  const showGoogleButton = !!(ssoConfig?.portal_google_enabled && ssoConfig.google_client_id
    && googleDisponible('portal'))
  const showPasswordForm = ssoConfig?.portal_password_login_allowed !== false
  const hayBotones = showGoogleButton || showSSOButton

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    try {
      const data = await loginMutation.mutateAsync({ email, password })
      setTokens(data.access, data.refresh, data.employee)
      navigate(enElPortal(tomarDestino(), 'colaboradores'), { replace: true })
    } catch (err) {
      setError(extractErrorMessage(err, 'Error inesperado') || 'Credenciales incorrectas.')
    }
  }

  const handleSSOLogin = async () => {
    setError(null)
    setSsoPending(true)
    try {
      const res = await apiClient.post('/auth/saml/init/', { audience: 'portal' })
      window.location.href = res.data.redirect_url
    } catch (err) {
      setSsoPending(false)
      setError(extractErrorMessage(err, 'No se pudo iniciar el ingreso con SSO.'))
    }
  }

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <EnvBanner />
      <div className="flex-1 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-content">Portal del Colaborador</h1>
          <p className="text-sm text-content-muted mt-1">
            {showPasswordForm
              ? 'Ingreso con la cuenta de colaborador'
              : 'Ingreso con la cuenta corporativa'}
          </p>
        </div>

        <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-6 shadow-sm">
          {error && (
            <div
              className="bg-danger/10 border border-danger/30 text-danger text-sm rounded-lg px-3 py-2 mb-4"
              role="alert"
            >
              {error}
            </div>
          )}

          {showGoogleButton && (
            <button
              type="button"
              onClick={() => {
                setError(null)
                iniciarLoginGoogle('portal').catch(() =>
                  setError('No se pudo iniciar el ingreso con Google. Intentar de nuevo.'))
              }}
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 mb-3
                         border border-divider-strong rounded-lg text-sm font-medium text-content
                         hover:bg-soft transition-colors
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
            >
              <GoogleIcon />
              Ingresar con Google
            </button>
          )}

          {showSSOButton && (
            <button
              type="button"
              onClick={handleSSOLogin}
              disabled={ssoPending}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4
                         border border-divider-strong rounded-lg text-sm font-medium text-content
                         hover:bg-soft transition-colors disabled:opacity-60
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
            >
              {ssoPending
                ? <Loader2 size={16} className="animate-spin" />
                : <ShieldCheck size={16} />}
              {ssoPending ? 'Redirigiendo…' : 'Ingresar con SSO corporativo'}
            </button>
          )}

          {hayBotones && showPasswordForm && (
            <div className="flex items-center gap-3 my-4" aria-hidden="true">
              <span className="h-px flex-1 bg-divider" />
              <span className="text-xs text-content-subtle">o</span>
              <span className="h-px flex-1 bg-divider" />
            </div>
          )}

          {!showPasswordForm && !ssoLoading && (
            <p className="mt-3 text-center text-xs text-content-muted">
              La empresa ingresa con la cuenta corporativa, sin contraseña.
            </p>
          )}

          {showPasswordForm && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="portal-email" className="block text-sm font-medium text-content mb-1">
                Email
              </label>
              <input
                id="portal-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-3 py-2 text-sm bg-input-bg border border-input-border rounded-lg
                           text-content placeholder:text-content-subtle
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                placeholder="tu@email.cl"
              />
            </div>

            <div>
              <label htmlFor="portal-password" className="block text-sm font-medium text-content mb-1">
                Contraseña
              </label>
              <input
                id="portal-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-3 py-2 text-sm bg-input-bg border border-input-border rounded-lg
                           text-content placeholder:text-content-subtle
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="w-full py-2.5 px-4 bg-primary hover:bg-primary-hover text-on-primary font-medium
                         rounded-lg text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loginMutation.isPending ? 'Ingresando...' : 'Ingresar al portal'}
            </button>
            <p className="text-center">
              <Link to="/colaboradores/recuperar" className="text-xs font-medium text-primary hover:underline">
                ¿Contraseña olvidada?
              </Link>
            </p>
          </form>
          )}
        </div>

        <p className="text-center text-xs text-content-subtle mt-6">
          ¿Problemas para ingresar? El área de RRHH de la empresa puede ayudar.
        </p>
      </div>
      </div>
    </div>
  )
}
